<template>
	<div>
		<nav class="main-nav hidden md:block">
			<div class="container">
				<div class="nav-content">
					<div class="flex items-center">
						<div class="brand-container">
							<router-link class="brand-link" to="/tracking">
								<img src="/favicon.ico" alt="App Icon" class="app-icon" />
								<span>{{ $t("appName") }}</span>
							</router-link>
						</div>
					</div>

					<!-- Desktop Navigation -->
					<div class="desktop-nav">
						<div class="desktop-nav-items">
							<nav-link :to="item.path" v-for="item in [...menuItems, ...hiddenForMobile]"
								:key="item.path">
								{{ $t(item.label) }}
							</nav-link>
							<button @click="handleFeedback" class="nav-link">
								<i class="pi pi-exclamation-triangle me-1"></i>
								{{ $t("feedback.reportBug") }}
							</button>
							<button @click="emit('logout')" class="nav-link">
								<i class="pi pi-sign-out me-1"></i>
								{{ $t("logout") }}
							</button>
						</div>
					</div>
				</div>
			</div>
		</nav>

		<!-- Mobile Bottom Navigation -->
		<nav
			class="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex justify-around py-2 z-50 shadow-lg md:hidden">
			<nav-link-bottom :to="item.path" v-for="item in menuItems" :key="item.path" :icon="item.icon">
				{{ $t(item.label) }}
			</nav-link-bottom>

			<!-- Settings Menu in Bottom Nav -->
			<div class="relative flex flex-col items-center">
				<button @click="isSettingsOpen = !isSettingsOpen"
					class="flex flex-col items-center p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all duration-200 min-w-0 max-w-16">
					<i class="pi pi-ellipsis-h text-xl mb-1"></i>
					<span class="text-xs font-medium text-center leading-tight truncate w-full">{{ $t("navigation.more")
					}}</span>
				</button>
				<div v-if="isSettingsOpen"
					class="absolute bottom-full right-0 mb-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-xl min-w-48 z-60"
					@click="isSettingsOpen = false">
					<template v-for="item in hiddenForMobile" :key="item.path">
						<router-link :to="item.path"
							class="flex items-center w-full px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 rounded-t-lg">
							<i :class="`pi ${item.icon} mr-2`"></i>
							{{ $t(item.label) }}
						</router-link>
					</template>
					<button @click="handleFeedback"
						class="flex items-center w-full px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 rounded-t-lg">
						<i class="pi pi-exclamation-triangle mr-2"></i>
						{{ $t("feedback.reportBug") }}
					</button>
					<button @click="emit('logout')"
						class="flex items-center w-full px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 rounded-b-lg">
						<i class="pi pi-sign-out mr-2"></i>
						{{ $t("logout") }}
					</button>
				</div>
			</div>
		</nav>
	</div>
</template>

<script setup lang="ts">
import { ref, defineComponent, h } from 'vue';
import { RouterLink } from 'vue-router';
import { getFeedback } from "@sentry/vue"
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const isSettingsOpen = ref(false);

const hiddenForMobile = [
	{ path: '/settings', label: 'navigation.route.settings', icon: 'pi-cog' },
	{ path: '/faq', label: 'navigation.route.faq', icon: 'pi-question-circle' }
]

const menuItems = [
	{ path: '/tracking', label: 'navigation.route.tracking', icon: 'pi-check-circle' },
	{ path: '/household', label: 'navigation.route.household', icon: 'pi-home' },
	{ path: '/prizes', label: 'navigation.route.prizes', icon: 'pi-star' },
	{ path: '/history', label: 'navigation.route.history', icon: 'pi-clock' }
];

const emit = defineEmits(['logout']);

// Feedback functionality
const handleFeedback = async () => {
	try {
		const feedback = getFeedback();
		if (feedback) {
			const form = await feedback.createForm({
				formTitle: t('feedback.formTitle'),
				addScreenshotButtonLabel: t('feedback.addScreenshotButtonLabel'),
				cancelButtonLabel: t('feedback.cancelButtonLabel'),
				confirmButtonLabel: t('feedback.confirmButtonLabel'),
				emailLabel: t('feedback.emailLabel'),
				emailPlaceholder: "oxly@domain.com",
				isRequiredLabel: t('feedback.isRequiredLabel'),
				nameLabel: t('feedback.nameLabel'),
				namePlaceholder: "Werner",
				messageLabel: t('feedback.messageLabel'),
				messagePlaceholder: t('feedback.messagePlaceholder'),
				submitButtonLabel: t('feedback.submitButtonLabel')
			});
			form.appendToDom();
			form.open();
		}
	} catch (error) {
		console.warn('Failed to open feedback form:', error);
	}
};

const NavLink = defineComponent({
	props: ['to'],
	setup(props, { slots }) {
		// For settings route, we want to match child routes too
		const isSettingsRoute = props.to === '/settings';

		return () => h(RouterLink, {
			to: props.to,
			class: 'nav-link',
			activeClass: 'nav-link-active',
			// Use exact matching for all routes except settings
			exact: !isSettingsRoute
		}, () => slots.default?.());
	}
});

const NavLinkBottom = defineComponent({
	props: ['to', 'icon'],
	setup(props, { slots }) {
		// For settings route, we want to match child routes too
		const isSettingsRoute = props.to === '/settings';

		return () => h(RouterLink, {
			to: props.to,
			class: 'flex flex-col items-center p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all duration-200 min-w-0 flex-1 max-w-16',
			activeClass: 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/30',
			// Use exact matching for all routes except settings
			exact: !isSettingsRoute
		}, () => [
			h('i', { class: `pi ${props.icon} text-xl mb-1` }),
			h('span', { class: 'text-xs font-medium text-center leading-tight truncate w-full' }, slots.default?.())
		]);
	}
});
</script>

<style scoped>
.main-nav {
	background-color: var(--navigation-background);
	border-bottom: 1px solid var(--navigation-border-color);
}

.container {
	max-width: 80rem;
	margin: 0 auto;
	padding: 0 1rem;
}

.nav-content {
	display: flex;
	align-items: center;
	justify-content: space-between;
	height: 4rem;
}

.app-icon {
	height: 3rem;
	width: auto;
	object-fit: contain;
}

.brand-link {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	color: var(--navigation-header-color);
	font-weight: 600;
	font-size: 1.125rem;
	text-decoration: none;
}

.desktop-nav {
	display: none;
}

.desktop-nav-items {
	display: flex;
	align-items: baseline;
	gap: 1rem;
}

.nav-link {
	color: var(--navigation-color);
	padding: var(--navigation-item-padding);
	border-radius: var(--navigation-item-border-radius);
	transition: background-color 0.2s, color 0.2s;
	display: inline-block;
	font-size: 0.875rem;
	font-weight: 500;
	text-decoration: none;
	border: none;
	background: none;
	cursor: pointer;
}

.nav-link:hover {
	background-color: var(--navigation-hover-background);
	color: var(--navigation-hover-color);
}

.nav-link-active {
	background-color: var(--p-primary-900);
	color: white;
}

.nav-link-active:hover {
	background-color: var(--p-primary-600);
	color: white;
}

@media (min-width: 768px) {
	.desktop-nav {
		display: flex;
		align-items: center;
	}
}
</style>